import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Car, Copy, Check, PhoneCall } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { REWARD_CODE } from "@/lib/api";
import { businessWaLink, WhatsAppIcon } from "@/lib/whatsapp";

export const SuccessDialog = ({ lead, onClose }) => {
  const [copied, setCopied] = useState(false);
  const firstName = lead?.nombre?.split(" ")[0] || "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(REWARD_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      setCopied(false);
    }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-3xl p-0 overflow-hidden border-0" data-testid="success-dialog">
        <div className="gradient-brand px-8 pt-10 pb-8 text-white text-center">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-white/20 ring-4 ring-white/30"
          >
            <CheckCircle2 className="h-9 w-9" />
          </motion.div>
          <DialogTitle className="mt-5 font-heading text-2xl font-extrabold" data-testid="success-title">
            ¡Listo, {firstName}! Recibimos tu solicitud
          </DialogTitle>
          <DialogDescription className="mt-2 text-white/90 text-sm">
            Un especialista de Mi Protección 365 te llamará muy pronto para preparar tu cotización gratis de IUL.
          </DialogDescription>
        </div>

        <div className="p-6 sm:p-8 bg-white">
          <p className="text-xs font-bold tracking-[0.2em] text-slate-500 uppercase text-center">Tu recompensa</p>
          <div className="mt-3 rounded-2xl border-2 border-dashed border-[var(--green-500)] bg-[var(--green-50)] p-5" data-testid="reward-card">
            <div className="flex items-center gap-4">
              <span className="grid place-items-center h-14 w-14 rounded-2xl bg-navy text-white shrink-0">
                <Car className="h-7 w-7" />
              </span>
              <div>
                <p className="font-heading text-3xl font-extrabold text-navy leading-none" data-testid="reward-amount">
                  $50 de descuento
                </p>
                <p className="text-sm text-slate-700 mt-1">en servicios de seguro de auto con nuestras cotizaciones</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-white border border-slate-200 px-4 py-3">
              <div>
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Código</p>
                <p className="font-mono font-extrabold tracking-[0.2em] text-navy" data-testid="reward-code">{REWARD_CODE}</p>
              </div>
              <button
                onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-navy text-white text-xs font-bold px-3 py-2 hover:bg-[var(--navy-800)] transition-colors"
                data-testid="reward-copy-button"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-600 leading-snug">
              Menciona este código al cotizar tu seguro de auto con nosotros para aplicar tu descuento.
            </p>
          </div>

          <div className="mt-5 flex items-center gap-2 text-xs text-slate-500 justify-center">
            <PhoneCall className="h-3.5 w-3.5" /> Te contactaremos al {lead?.telefono}
          </div>

          <a
            href={businessWaLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold py-3 transition-colors"
            data-testid="success-whatsapp-button"
          >
            <WhatsAppIcon className="h-5 w-5" /> ¿Prefieres escribirnos ahora? WhatsApp
          </a>

          <button
            onClick={onClose}
            className="mt-3 w-full rounded-xl bg-slate-100 hover:bg-slate-200 text-navy font-bold py-3 transition-colors"
            data-testid="success-close-button"
          >
            Entendido
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
